<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import ConversationStagesApi from 'dashboard/api/conversationStages';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isLoading = ref(false);
const isSubmitting = ref(false);
const stages = ref([]);
const editingStageId = ref(null);
const createForm = ref({
  name: '',
  emoji: '',
  description: '',
});
const editForm = ref({
  name: '',
  emoji: '',
  description: '',
});

const canDrag = computed(() => stages.value.length > 1);

const sortByPosition = list => {
  return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
};

const normalizePayload = form => ({
  name: form.name.trim(),
  emoji: form.emoji.trim(),
  description: form.description?.trim() || '',
});

const fetchStages = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationStagesApi.get();
    stages.value = sortByPosition(data);
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const resetCreateForm = () => {
  createForm.value = {
    name: '',
    emoji: '',
    description: '',
  };
};

const createStage = async () => {
  if (!createForm.value.name.trim() || !createForm.value.emoji.trim()) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.INVALID_FORM'));
    return;
  }

  isSubmitting.value = true;
  try {
    const { data } = await ConversationStagesApi.create({
      conversation_stage: normalizePayload(createForm.value),
    });
    stages.value = sortByPosition([...stages.value, data]);
    resetCreateForm();
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const startEdit = stage => {
  editingStageId.value = stage.id;
  editForm.value = {
    name: stage.name,
    emoji: stage.emoji,
    description: stage.description || '',
  };
};

const cancelEdit = () => {
  editingStageId.value = null;
  editForm.value = {
    name: '',
    emoji: '',
    description: '',
  };
};

const updateStage = async stageId => {
  if (!editForm.value.name.trim() || !editForm.value.emoji.trim()) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.INVALID_FORM'));
    return;
  }

  isSubmitting.value = true;
  try {
    const { data } = await ConversationStagesApi.update(stageId, {
      conversation_stage: normalizePayload(editForm.value),
    });

    stages.value = sortByPosition(
      stages.value.map(stage => (stage.id === stageId ? data : stage))
    );
    cancelEdit();
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.UPDATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const deleteStage = async stage => {
  const shouldDelete = window.confirm(
    t('CONVERSATION_WORKFLOW.STAGES.CONFIRM_DELETE', {
      stage: stage.name,
    })
  );

  if (!shouldDelete) {
    return;
  }

  isSubmitting.value = true;
  try {
    await ConversationStagesApi.delete(stage.id);
    stages.value = stages.value.filter(item => item.id !== stage.id);
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.DELETE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const reorderStages = async () => {
  if (!canDrag.value) {
    return;
  }

  const previousStages = [...stages.value];
  const conversationStageIds = stages.value.map(stage => stage.id);

  try {
    const { data } = await ConversationStagesApi.reorder(conversationStageIds);
    stages.value = sortByPosition(data);
  } catch (error) {
    stages.value = previousStages;
    useAlert(t('CONVERSATION_WORKFLOW.STAGES.API.REORDER_ERROR'));
  }
};

onMounted(() => {
  fetchStages();
});
</script>

<template>
  <section class="p-4 border rounded-lg border-n-weak bg-n-solid-2">
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ $t('CONVERSATION_WORKFLOW.STAGES.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION_WORKFLOW.STAGES.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid gap-2 mt-4 md:grid-cols-[1fr_8rem_1.4fr_auto]">
      <input
        v-model="createForm.name"
        type="text"
        class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
        :placeholder="$t('CONVERSATION_WORKFLOW.STAGES.FORM.NAME_PLACEHOLDER')"
      />
      <input
        v-model="createForm.emoji"
        type="text"
        class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
        :placeholder="$t('CONVERSATION_WORKFLOW.STAGES.FORM.EMOJI_PLACEHOLDER')"
      />
      <input
        v-model="createForm.description"
        type="text"
        class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
        :placeholder="
          $t('CONVERSATION_WORKFLOW.STAGES.FORM.DESCRIPTION_PLACEHOLDER')
        "
      />
      <NextButton
        sm
        icon="i-lucide-plus"
        :label="$t('CONVERSATION_WORKFLOW.STAGES.FORM.CREATE')"
        :disabled="isSubmitting"
        @click="createStage"
      />
    </div>

    <p class="mt-3 text-xs text-n-slate-11">
      {{ $t('CONVERSATION_WORKFLOW.STAGES.REORDER_HELP') }}
    </p>

    <div v-if="isLoading" class="py-6 text-sm text-center text-n-slate-11">
      {{ $t('CONVERSATION_WORKFLOW.STAGES.LOADING') }}
    </div>

    <Draggable
      v-else
      v-model="stages"
      item-key="id"
      handle=".drag-handle"
      class="flex flex-col gap-2 mt-3"
      :disabled="!canDrag"
      @end="reorderStages"
    >
      <template #item="{ element }">
        <div
          class="flex items-start gap-2 p-3 rounded-md bg-n-alpha-1 border border-n-weak"
        >
          <button
            type="button"
            class="mt-1 text-n-slate-10 drag-handle"
            :title="$t('CONVERSATION_WORKFLOW.STAGES.REORDER_HANDLE')"
          >
            ::
          </button>

          <div class="flex-1 min-w-0">
            <div
              v-if="editingStageId === element.id"
              class="grid gap-2 md:grid-cols-[1fr_8rem_1.4fr]"
            >
              <input
                v-model="editForm.name"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
              />
              <input
                v-model="editForm.emoji"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
              />
              <input
                v-model="editForm.description"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-md bg-n-alpha-2 border border-n-weak"
              />
            </div>
            <div v-else>
              <p class="text-sm font-medium text-n-slate-12">
                {{ element.emoji }} {{ element.name }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ element.description || '-' }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-1">
            <template v-if="editingStageId === element.id">
              <NextButton
                link
                xs
                icon="i-lucide-check"
                :label="$t('CONVERSATION_WORKFLOW.STAGES.ACTIONS.SAVE')"
                :disabled="isSubmitting"
                @click="updateStage(element.id)"
              />
              <NextButton
                link
                xs
                icon="i-lucide-x"
                :label="$t('CONVERSATION_WORKFLOW.STAGES.ACTIONS.CANCEL')"
                :disabled="isSubmitting"
                @click="cancelEdit"
              />
            </template>
            <template v-else>
              <NextButton
                link
                xs
                icon="i-lucide-pencil"
                :label="$t('CONVERSATION_WORKFLOW.STAGES.ACTIONS.EDIT')"
                :disabled="isSubmitting"
                @click="startEdit(element)"
              />
              <NextButton
                link
                xs
                icon="i-lucide-trash"
                :label="$t('CONVERSATION_WORKFLOW.STAGES.ACTIONS.DELETE')"
                :disabled="isSubmitting"
                @click="deleteStage(element)"
              />
            </template>
          </div>
        </div>
      </template>
    </Draggable>

    <p v-if="!isLoading && !stages.length" class="mt-4 text-sm text-n-slate-11">
      {{ $t('CONVERSATION_WORKFLOW.STAGES.EMPTY') }}
    </p>
  </section>
</template>
